/**
 * Utilities for handling number and financial value operations
 */

/**
 * Parses a financial value from various formats into a number
 * Handles both string and number inputs, removes currency symbols and formats
 * 
 * @param {string|number} value - The financial value to parse
 * @returns {number} - The parsed number value
 */
export const parseFinancialValue = (value) => {
  // Return 0 if value is null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  // If value is already a number, return it
  if (typeof value === 'number') {
    return value;
  }
  
  // If value is a string, process it
  if (typeof value === 'string') {
    // Remove currency symbols, spaces, and commas
    let cleanValue = value.replace(/[R$€£\s,]/g, '');
    
    // Replace commas used as decimal separators with periods
    if (cleanValue.includes(',') && !cleanValue.includes('.')) {
      cleanValue = cleanValue.replace(',', '.');
    }
    
    // Parse to float and ensure it's a valid number
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  // If we get here, the value is of an unexpected type
  console.warn('parseFinancialValue received unexpected value type:', typeof value);
  return 0;
};

/**
 * Formats a number as a currency string with proper localization
 * 
 * @param {number} value - The number to format
 * @param {string} locale - The locale to use for formatting (default: 'en-ZA')
 * @param {string} currency - The currency code (default: 'ZAR')
 * @returns {string} - The formatted currency string
 */
export const formatCurrency = (value, locale = 'en-ZA', currency = 'ZAR') => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  });
  
  return formatter.format(parseFinancialValue(value));
};

/**
 * Rounds a number to a specified number of decimal places
 * 
 * @param {number} value - The number to round
 * @param {number} decimals - The number of decimal places (default: 2)
 * @returns {number} - The rounded number
 */
export const roundNumber = (value, decimals = 2) => {
  const multiplier = Math.pow(10, decimals);
  return Math.round(parseFinancialValue(value) * multiplier) / multiplier;
};
